import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card, CardContent,
  Checkbox, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControlLabel, Grid, LinearProgress, MenuItem, Stack, Step, StepButton, Stepper,
  IconButton, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SaveIcon from '@mui/icons-material/Save';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { Helmet } from 'react-helmet-async';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

import config from '@src/config';
import leadGurusLogo from '@src/assets/logo-full-dark.png';
import { toast } from '@src/components/core/toaster';
import defaultClient from '@src/api/clients/default';
import { useAuth } from '@src/context';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const TIME_SLOTS = [
  '6:00 AM', '6:30 AM', '7:00 AM', '7:30 AM', '8:00 AM', '8:30 AM',
  '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
  '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM',
  '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM', '5:00 PM', '5:30 PM',
  '6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM', '8:00 PM', '8:30 PM', '9:00 PM',
];

function parseSchedule(str) {
  const schedule = {};
  DAYS.forEach((d) => { schedule[d] = { enabled: false, slots: [] }; });
  if (!str) return schedule;
  str.split(';').forEach((part) => {
    const trimmed = part.trim();
    const colonIdx = trimmed.indexOf(':');
    if (colonIdx === -1) return;
    const dayStr = trimmed.slice(0, colonIdx).trim();
    const slotsStr = trimmed.slice(colonIdx + 1).trim();
    const day = DAYS.find((d) => d.toLowerCase().startsWith(dayStr.toLowerCase()));
    if (day && slotsStr) {
      const slots = slotsStr.split(',').map((s) => s.trim()).filter(Boolean);
      schedule[day] = { enabled: true, slots };
    }
  });
  return schedule;
}

function serializeSchedule(schedule) {
  return DAYS
    .filter((d) => schedule[d]?.enabled && schedule[d].slots.length > 0)
    .map((d) => `${d}: ${schedule[d].slots.join(', ')}`)
    .join('; ');
}

function SalesScheduleGrid({ value, onChange, compact }) {
  const [schedule, setSchedule] = useState(() => parseSchedule(value));

  // Sync if value changes externally (e.g. "Apply to All")
  useEffect(() => {
    setSchedule(parseSchedule(value));
  }, [value]);

  const update = (newSchedule) => {
    setSchedule(newSchedule);
    onChange(serializeSchedule(newSchedule));
  };

  const handleToggle = (day) => {
    update({ ...schedule, [day]: { ...schedule[day], enabled: !schedule[day].enabled, slots: schedule[day].slots || [] } });
  };

  const handleAddSlot = (day, time) => {
    if (!time || schedule[day].slots.includes(time)) return;
    const newSlots = [...schedule[day].slots, time].sort((a, b) => TIME_SLOTS.indexOf(a) - TIME_SLOTS.indexOf(b));
    update({ ...schedule, [day]: { ...schedule[day], slots: newSlots } });
  };

  const handleRemoveSlot = (day, time) => {
    update({ ...schedule, [day]: { ...schedule[day], slots: schedule[day].slots.filter((s) => s !== time) } });
  };

  const applyToAll = (day) => {
    const source = schedule[day].slots;
    const newSchedule = { ...schedule };
    DAYS.forEach((d) => {
      if (newSchedule[d].enabled) {
        newSchedule[d] = { ...newSchedule[d], slots: [...source] };
      }
    });
    update(newSchedule);
  };

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">Availability</Typography>
      <Typography variant="caption" color="text.secondary">
        Select the days your team is available and add the time slots.
      </Typography>
      {DAYS.map((day) => (
        <Box key={day} sx={{ py: 0.5, px: 1, borderRadius: 1, bgcolor: schedule[day].enabled ? 'grey.50' : 'transparent' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FormControlLabel
              control={<Checkbox size="small" checked={schedule[day].enabled} onChange={() => handleToggle(day)} />}
              label={<Typography variant="body2" sx={{ width: 55, fontWeight: schedule[day].enabled ? 600 : 400, fontSize: 13 }}>{day.slice(0, 3)}</Typography>}
              sx={{ m: 0 }}
            />
            {schedule[day].enabled ? (
              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
                {schedule[day].slots.map((slot) => (
                  <Chip key={slot} label={slot} size="small" onDelete={() => handleRemoveSlot(day, slot)} sx={{ fontSize: 11 }} />
                ))}
                <TextField
                  select size="small" value="" displayEmpty
                  onChange={(e) => handleAddSlot(day, e.target.value)}
                  sx={{ minWidth: 100 }}
                  SelectProps={{ native: true }}
                >
                  <option value="">+ Add</option>
                  {TIME_SLOTS.filter((t) => !schedule[day].slots.includes(t)).map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </TextField>
                {schedule[day].slots.length > 0 && (
                  <Button size="small" onClick={() => applyToAll(day)}
                    sx={{ fontSize: 10, textTransform: 'none', whiteSpace: 'nowrap' }}>
                    Apply to all days
                  </Button>
                )}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.disabled" sx={{ fontSize: 13 }}>Closed</Typography>
            )}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

const STEPS = [
  { key: 'basics', label: 'Contact Info' },
  { key: 'billing', label: 'Billing' },
  { key: 'websites', label: 'Websites' },
  { key: 'assets', label: 'Assets' },
  { key: 'offers', label: 'Offers & Messaging' },
  { key: 'schedule', label: 'Sales Schedule' },
  { key: 'review', label: 'Review' },
];

function fetchParentOnboarding(slug) {
  return defaultClient.request({ url: `parent-companies/${slug}/onboarding/` });
}
function saveParentOnboarding(slug, payload) {
  return defaultClient.request({ url: `parent-companies/${slug}/onboarding/`, method: 'PATCH', payload });
}
function fetchParentTimeline(slug) {
  return defaultClient.request({ url: `parent-companies/${slug}/onboarding/timeline/` });
}

// ── Stripe Card Form ─────────────────────────────────────

function CardCollectionForm({ clientSecret, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSaving(true);
    setError(null);
    const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
      payment_method: { card: elements.getElement(CardElement) },
    });
    if (stripeError) { setError(stripeError.message); setSaving(false); return; }
    if (setupIntent?.status === 'succeeded') {
      try {
        const pm = await stripe.retrievePaymentMethod(setupIntent.payment_method);
        const card = pm?.paymentMethod?.card || {};
        onSuccess({ brand: card.brand || '', last4: card.last4 || '', exp_month: card.exp_month, exp_year: card.exp_year });
        toast.success('Card saved successfully!');
      } catch { onSuccess(null); toast.success('Card saved!'); }
    }
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2, bgcolor: '#fafafa' }}>
        <CardElement options={{ style: { base: { fontSize: '16px', color: '#333', fontFamily: 'Arial, sans-serif', '::placeholder': { color: '#999' } }, invalid: { color: '#dc2626' } } }} />
      </Box>
      {error && <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>{error}</Typography>}
      <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
        <Button type="submit" variant="contained" disabled={!stripe || saving} sx={{ bgcolor: '#ff680d', '&:hover': { bgcolor: '#ea580c' } }}>
          {saving ? 'Saving...' : 'Save Card'}
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>Secured by Stripe.</Typography>
      </Stack>
    </form>
  );
}

// ── Main Component ───────────────────────────────────────

export default function ParentOnboardingWizardPage() {
  const { parentSlug } = useParams();
  const { state: authState, logout } = useAuth();
  const [parentCompany, setParentCompany] = useState(null);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [offerOptions, setOfferOptions] = useState({});
  const [clientVerticals, setClientVerticals] = useState([]);
  const [timeline, setTimeline] = useState(null);
  const [completion, setCompletion] = useState({});

  // Invite
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);

  // Stripe
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [cardSaved, setCardSaved] = useState(false);
  const [cardStatus, setCardStatus] = useState({});

  // Form state
  const [form, setForm] = useState({
    basics: { firstName: '', lastName: '', email: '', position: '' },
    secondaryPocs: [],
    billingContact: { name: '', email: '', phone: '', addressLine1: '', addressLine2: '', city: '', state: '', postalCode: '', country: '' },
    billingAcknowledged: false,
    evergreenOffer: '',
    financingOffer: '',
    offers: {},
    launchDate: null,
    brandAssets: {}, // {child_slug: {assets_folder_link, use_stock_assets, files: []}}
  });

  // ── Load ────────────────────────────────────────────────

  useEffect(() => {
    if (!parentSlug) return;
    setLoading(true);
    Promise.all([
      fetchParentOnboarding(parentSlug).catch(() => null),
      fetchParentTimeline(parentSlug).catch(() => null),
    ]).then(([onbRes, tlRes]) => {
      const onb = onbRes?.data;
      if (tlRes?.data) setTimeline(tlRes.data);
      if (onb) {
        setParentCompany(onb.parent_company);
        setChildren(onb.children || []);
        if (onb.offer_options) setOfferOptions(onb.offer_options);
        if (onb.client_verticals) setClientVerticals(onb.client_verticals);
        setCompletion(onb.completion || {});

        // Load existing offers
        const existingOffers = {};
        if (onb.client_offers) {
          onb.client_offers.forEach((o) => {
            const key = `${o.vertical_id}_${o.offer_type}`;
            existingOffers[key] = {
              vertical_id: o.vertical_id, offer_type: o.offer_type,
              ad_offer_option_id: o.ad_offer_option_id, custom_label: o.custom_label || '',
              financing_details: o.financing_details || '',
            };
          });
        }

        // Build brand data from children
        const brandAssets = {};
        (onb.children || []).forEach((child) => {
          brandAssets[child.slug] = {
            assets_folder_link: child.assets_folder_link || '',
            use_stock_assets: child.use_stock_assets || false,
            existingAssets: child.assets || [],
            files: [],
            salesSchedule: child.sales_schedule || '',
            websiteUrl: child.website_url || '',
          };
        });

        setForm((prev) => ({
          ...prev,
          basics: {
            firstName: onb.basics?.firstName || '',
            lastName: onb.basics?.lastName || '',
            email: onb.basics?.email || '',
            position: onb.basics?.position || '',
          },
          secondaryPocs: onb.secondary_pocs || [],
          billingContact: {
            name: onb.billing_contact?.name || '',
            email: onb.billing_contact?.email || '',
            phone: onb.billing_contact?.phone || '',
            addressLine1: onb.billing_contact?.addressLine1 || '',
            addressLine2: onb.billing_contact?.addressLine2 || '',
            city: onb.billing_contact?.city || '',
            state: onb.billing_contact?.state || '',
            postalCode: onb.billing_contact?.postalCode || '',
            country: onb.billing_contact?.country || '',
          },
          billingAcknowledged: onb.billing_acknowledged || false,
          evergreenOffer: onb.evergreen_offer || '',
          financingOffer: onb.financing_offer || '',
          offers: Object.keys(existingOffers).length ? existingOffers : (onb.offers || {}),
          launchDate: onb.launch_goal ? dayjs(onb.launch_goal) : null,
          brandAssets,
        }));

        // Check if already submitted (all basics done)
        const comp = onb.completion || {};
        if (comp.basics && comp.offers) setSubmitted(true);
      }
    }).finally(() => setLoading(false));
  }, [parentSlug]);

  // Stripe card status from first child
  const firstChildSlug = children[0]?.slug;
  useEffect(() => {
    if (!firstChildSlug) return;
    defaultClient.request({ url: `app1/setup-intent/${firstChildSlug}/` })
      .then((res) => {
        const data = res?.data || {};
        setCardStatus(data);
        if (data.has_card) setCardSaved(true);
        if (data.publishable_key) setStripePromise((prev) => prev || loadStripe(data.publishable_key));
      })
      .catch(() => setCardStatus({ has_card: false }));
  }, [firstChildSlug]);

  // ── Save ────────────────────────────────────────────────

  const handleSave = useCallback(async (isSubmission = false) => {
    setSaving(true);
    try {
      const payload = {
        basics: form.basics,
        secondary_pocs: form.secondaryPocs,
        billing_contact: form.billingContact,
        billing_acknowledged: form.billingAcknowledged,
        evergreen_offer: form.evergreenOffer,
        financing_offer: form.financingOffer,
        offers: form.offers || {},
        launch_goal: form.launchDate ? form.launchDate.format('YYYY-MM-DD') : null,
        brand_assets: {},
      };

      // Build brand_assets (per-child data)
      Object.entries(form.brandAssets).forEach(([slug, data]) => {
        payload.brand_assets[slug] = {
          assets_folder_link: data.assets_folder_link || '',
          use_stock_assets: data.use_stock_assets || false,
          sales_schedule: data.salesSchedule || '',
          website_url: data.websiteUrl || '',
        };
      });

      if (isSubmission) payload._submitted = true;

      // Check if any child has file uploads
      const hasFiles = Object.values(form.brandAssets).some((ba) => ba.files?.length > 0);

      if (hasFiles) {
        const formData = new FormData();
        formData.append('data', JSON.stringify(payload));
        // Also set top-level fields for the multipart parser
        Object.entries(payload).forEach(([key, val]) => {
          if (key === 'brand_assets') return;
          if (typeof val === 'object' && val !== null) {
            formData.append(key, JSON.stringify(val));
          } else if (val !== null && val !== undefined) {
            formData.append(key, val);
          }
        });
        formData.append('brand_assets', JSON.stringify(payload.brand_assets));
        // Append files per child
        Object.entries(form.brandAssets).forEach(([slug, data]) => {
          (data.files || []).forEach((f) => formData.append(`assets_${slug}`, f));
        });
        await defaultClient.request({ url: `parent-companies/${parentSlug}/onboarding/`, method: 'PATCH', payload: formData });
      } else {
        await saveParentOnboarding(parentSlug, payload);
      }

      // Clear file buffers after successful upload
      setForm((prev) => {
        const newBrandAssets = { ...prev.brandAssets };
        Object.keys(newBrandAssets).forEach((slug) => {
          newBrandAssets[slug] = { ...newBrandAssets[slug], files: [] };
        });
        return { ...prev, brandAssets: newBrandAssets };
      });

      toast.success('Progress saved!');
      const tlRes = await fetchParentTimeline(parentSlug).catch(() => null);
      if (tlRes?.data) setTimeline(tlRes.data);
      // Refresh onboarding to get updated assets
      const onbRes = await fetchParentOnboarding(parentSlug).catch(() => null);
      if (onbRes?.data) {
        setChildren(onbRes.data.children || []);
        setCompletion(onbRes.data.completion || {});
        const brandAssets = {};
        (onbRes.data.children || []).forEach((child) => {
          brandAssets[child.slug] = {
            assets_folder_link: child.assets_folder_link || '',
            use_stock_assets: child.use_stock_assets || false,
            existingAssets: child.assets || [],
            files: [],
            salesSchedule: child.sales_schedule || '',
            websiteUrl: child.website_url || '',
          };
        });
        setForm((prev) => ({ ...prev, brandAssets }));
      }
    } catch {
      toast.error('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [parentSlug, form]);

  const handleNext = async () => {
    await handleSave();
    setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };
  const handleBack = () => setActiveStep((prev) => Math.max(prev - 1, 0));

  // ── Invite ──────────────────────────────────────────────

  const handleInviteMember = async () => {
    if (!inviteEmail) { toast.error('Email is required'); return; }
    setInviting(true);
    try {
      const [firstName, ...rest] = (inviteName || '').split(' ');
      await defaultClient.request({
        url: 'accounts/auth/client-memberships/invite/',
        method: 'POST',
        payload: {
          email: inviteEmail,
          first_name: firstName || '',
          last_name: rest.join(' ') || '',
          role: 'editor',
          parent_company_id: parentCompany?.id,
        },
      });
      toast.success('Invite sent!');
      setInviteOpen(false);
      setInviteEmail('');
      setInviteName('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  // ── Stripe setup ────────────────────────────────────────

  const handleSetupCard = async () => {
    if (!firstChildSlug) { toast.error('No child clients found'); return; }
    try {
      const res = await defaultClient.request({ url: `app1/setup-intent/${firstChildSlug}/`, method: 'POST' });
      setClientSecret(res?.data?.client_secret);
      if (res?.data?.publishable_key) setStripePromise((prev) => prev || loadStripe(res.data.publishable_key));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to initialize card setup.');
    }
  };

  // ── Helpers ─────────────────────────────────────────────

  const updateBasics = (field) => (e) => setForm((prev) => ({ ...prev, basics: { ...prev.basics, [field]: e.target.value } }));
  const updateBilling = (field) => (e) => setForm((prev) => ({ ...prev, billingContact: { ...prev.billingContact, [field]: e.target.value } }));
  const parentName = parentCompany?.name || 'Your Company';
  const brandNames = children.map((c) => c.name).join(', ');

  // ── Loading ─────────────────────────────────────────────

  if (loading) return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress size={48} />
    </Box>
  );

  // ── SUBMITTED VIEW ──────────────────────────────────────

  if (submitted) {
    const guruProgress = timeline?.guru_steps || [];
    const clientActions = timeline?.client_actions || [];
    const leadDeliverySummary = timeline?.lead_delivery_summary || [];
    const activity = timeline?.activity || [];
    const estimatedLaunch = timeline?.estimated_launch;

    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
        <Helmet><title>Onboarding Complete | {config.SITE.title}</title></Helmet>
        {/* Header */}
        <Box sx={{ bgcolor: '#111827', py: 2, px: 3 }}>
          <Container maxWidth="lg">
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <img src={leadGurusLogo} alt="Lead Gurus" style={{ height: 28 }} />
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="body2" sx={{ color: '#9ca3af' }}>{authState?.user?.email || parentName}</Typography>
                <Button size="small" startIcon={<LogoutIcon />} onClick={logout} sx={{ color: '#9ca3af', textTransform: 'none', fontSize: 12 }}>Logout</Button>
              </Stack>
            </Stack>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 72, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" fontWeight={700} gutterBottom>Onboarding Submitted!</Typography>
            <Typography color="text.secondary" sx={{ maxWidth: 500, mx: 'auto', mb: 2 }}>
              Thank you for completing your onboarding for <strong>{parentName}</strong>. Our team has been notified and is getting started on your campaigns.
            </Typography>
            <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" useFlexGap>
              <Button variant="outlined" size="small" onClick={() => setSubmitted(false)}>Edit Onboarding</Button>
              <Button variant="outlined" size="small" startIcon={<PersonAddIcon />} onClick={() => { setInviteEmail(''); setInviteName(''); setInviteOpen(true); }}>Invite Team Member</Button>
            </Stack>
          </Box>

          {/* Invite dialog */}
          <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Invite a team member to access the portal for all brands under {parentName}.
                </Typography>
                <TextField label="Their Name" value={inviteName} onChange={(e) => setInviteName(e.target.value)} fullWidth size="small" />
                <TextField label="Their Email" type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} fullWidth size="small" required />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setInviteOpen(false)}>Cancel</Button>
              <Button onClick={handleInviteMember} variant="contained" disabled={inviting || !inviteEmail}>{inviting ? 'Sending...' : 'Send Invite'}</Button>
            </DialogActions>
          </Dialog>

          {/* Guru progress stepper */}
          {guruProgress.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ py: 2.5, px: 3 }}>
                <Stepper activeStep={guruProgress.findIndex((p) => p.progress > 0 && p.progress < 100)} alternativeLabel
                  sx={{ '& .MuiStepConnector-line': { borderTopWidth: 3 }, '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': { borderColor: '#16a34a' }, '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': { borderColor: '#ff680d' } }}>
                  {guruProgress.map((phase) => {
                    const done = phase.progress === 100;
                    const active = phase.progress > 0 && phase.progress < 100;
                    return (
                      <Step key={phase.phase} completed={done}>
                        <StepButton
                          icon={done ? <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 28 }} /> : active ? <CircularProgress variant="determinate" value={phase.progress} size={24} thickness={5} sx={{ color: '#ff680d' }} /> : <RadioButtonUncheckedIcon sx={{ color: '#ccc', fontSize: 24 }} />}
                          sx={{ cursor: 'default' }}
                        >
                          <Typography variant="caption" fontWeight={done || active ? 700 : 400} sx={{ color: done ? '#16a34a' : active ? '#ff680d' : 'text.secondary', fontSize: 11 }}>
                            {phase.phase}
                          </Typography>
                        </StepButton>
                      </Step>
                    );
                  })}
                </Stepper>
              </CardContent>
            </Card>
          )}

          {/* Estimated launch */}
          {estimatedLaunch && (
            <Card sx={{ mb: 3, textAlign: 'center', py: 2 }}>
              <CardContent>
                <Typography variant="overline" color="text.secondary">Estimated Launch Date</Typography>
                <Typography variant="h4" fontWeight={700} color="primary">{dayjs(estimatedLaunch).format('MMMM D, YYYY')}</Typography>
              </CardContent>
            </Card>
          )}

          {/* Client actions */}
          {clientActions.length > 0 && (
            <Card sx={{ mb: 3, border: clientActions.some((a) => a.status === 'action_required') ? '2px solid #ff680d' : undefined }}>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  {clientActions.some((a) => a.status === 'action_required') && <Chip label="ACTION REQUIRED" size="small" sx={{ bgcolor: '#ff680d', color: '#fff', fontWeight: 700, fontSize: 10 }} />}
                  <Typography variant="subtitle1" fontWeight={700}>Your To-Dos</Typography>
                </Stack>
                <Stack spacing={1.5}>
                  {clientActions.map((action) => (
                    <Box key={action.key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: '#fafafa', borderRadius: 1 }}>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>{action.label}</Typography>
                        <Typography variant="caption" color="text.secondary">{action.description}</Typography>
                      </Box>
                      {action.status === 'action_required' && <Chip label="Setup" size="small" color="warning" variant="outlined" />}
                      {action.status === 'complete' && <CheckCircleIcon sx={{ color: '#16a34a' }} />}
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Lead delivery summary table */}
          {leadDeliverySummary.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Lead Delivery by Brand</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Brand</strong></TableCell>
                      <TableCell><strong>Webhook</strong></TableCell>
                      <TableCell><strong>Test</strong></TableCell>
                      <TableCell><strong>Confirmed</strong></TableCell>
                      <TableCell align="center"><strong>Action</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {leadDeliverySummary.map((ld) => (
                      <TableRow key={ld.client_slug} hover>
                        <TableCell>{ld.client_name}</TableCell>
                        <TableCell>{ld.has_url ? <Chip label="Configured" size="small" color="success" variant="outlined" /> : <Chip label="Not set" size="small" variant="outlined" />}</TableCell>
                        <TableCell>{ld.tested ? <Chip label="Passed" size="small" color="success" variant="outlined" /> : <Chip label="Pending" size="small" variant="outlined" />}</TableCell>
                        <TableCell>{ld.confirmed ? <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 20 }} /> : <RadioButtonUncheckedIcon sx={{ color: '#ccc', fontSize: 20 }} />}</TableCell>
                        <TableCell align="center">
                          <Button size="small" href={`/clients/${ld.client_slug}/webhook-setup`} target="_blank" endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}>
                            Configure
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Activity feed */}
          {activity.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Recent Activity</Typography>
                <Stack spacing={1}>
                  {activity.map((a, i) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{a.label}</Typography>
                        {a.detail && <Typography variant="caption" color="text.secondary">{a.detail}</Typography>}
                      </Box>
                      <Typography variant="caption" color="text.secondary">{dayjs(a.date).format('MMM D [at] h:mm A')}</Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}
        </Container>
      </Box>
    );
  }

  // ── WIZARD VIEW ─────────────────────────────────────────

  const stepContent = () => {
    switch (STEPS[activeStep]?.key) {
      case 'basics': return (
        <Stack spacing={3}>
          <Typography variant="h6">Contact Information</Typography>
          <Typography variant="body2" color="text.secondary">Who is the main point of contact for {parentName}?</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><TextField label="First Name" value={form.basics.firstName} onChange={updateBasics('firstName')} fullWidth required /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Last Name" value={form.basics.lastName} onChange={updateBasics('lastName')} fullWidth required /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Email" type="email" value={form.basics.email} onChange={updateBasics('email')} fullWidth required /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Position / Title" value={form.basics.position} onChange={updateBasics('position')} fullWidth /></Grid>
          </Grid>
          <Divider />
          <Typography variant="subtitle1" fontWeight={600}>Secondary Contacts (Optional)</Typography>
          {form.secondaryPocs.map((poc, i) => (
            <Grid container spacing={2} key={i} alignItems="center">
              <Grid item xs={12} sm={3}><TextField label="First Name" value={poc.firstName || ''} onChange={(e) => { const pocs = [...form.secondaryPocs]; pocs[i] = { ...pocs[i], firstName: e.target.value }; setForm((prev) => ({ ...prev, secondaryPocs: pocs })); }} fullWidth size="small" /></Grid>
              <Grid item xs={12} sm={3}><TextField label="Last Name" value={poc.lastName || ''} onChange={(e) => { const pocs = [...form.secondaryPocs]; pocs[i] = { ...pocs[i], lastName: e.target.value }; setForm((prev) => ({ ...prev, secondaryPocs: pocs })); }} fullWidth size="small" /></Grid>
              <Grid item xs={12} sm={3}><TextField label="Email" value={poc.email || ''} onChange={(e) => { const pocs = [...form.secondaryPocs]; pocs[i] = { ...pocs[i], email: e.target.value }; setForm((prev) => ({ ...prev, secondaryPocs: pocs })); }} fullWidth size="small" /></Grid>
              <Grid item xs={12} sm={2}><TextField label="Title" value={poc.position || ''} onChange={(e) => { const pocs = [...form.secondaryPocs]; pocs[i] = { ...pocs[i], position: e.target.value }; setForm((prev) => ({ ...prev, secondaryPocs: pocs })); }} fullWidth size="small" /></Grid>
              <Grid item xs={12} sm={1}><IconButton onClick={() => setForm((prev) => ({ ...prev, secondaryPocs: prev.secondaryPocs.filter((_, j) => j !== i) }))} size="small"><DeleteIcon /></IconButton></Grid>
            </Grid>
          ))}
          <Button startIcon={<AddIcon />} onClick={() => setForm((prev) => ({ ...prev, secondaryPocs: [...prev.secondaryPocs, { firstName: '', lastName: '', email: '', position: '' }] }))} sx={{ alignSelf: 'flex-start' }}>
            Add Secondary Contact
          </Button>
        </Stack>
      );

      case 'billing': return (
        <Stack spacing={3}>
          <Typography variant="h6">Billing Information</Typography>
          <Typography variant="body2" color="text.secondary">Who should receive invoices and billing communications for all brands?</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><TextField label="Billing Contact Name" value={form.billingContact.name} onChange={updateBilling('name')} fullWidth required /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Billing Email" type="email" value={form.billingContact.email} onChange={updateBilling('email')} fullWidth required /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Phone" value={form.billingContact.phone} onChange={updateBilling('phone')} fullWidth /></Grid>
            <Grid item xs={12}><TextField label="Address Line 1" value={form.billingContact.addressLine1} onChange={updateBilling('addressLine1')} fullWidth /></Grid>
            <Grid item xs={12}><TextField label="Address Line 2" value={form.billingContact.addressLine2} onChange={updateBilling('addressLine2')} fullWidth /></Grid>
            <Grid item xs={12} sm={4}><TextField label="City" value={form.billingContact.city} onChange={updateBilling('city')} fullWidth /></Grid>
            <Grid item xs={12} sm={4}><TextField label="State" value={form.billingContact.state} onChange={updateBilling('state')} fullWidth /></Grid>
            <Grid item xs={12} sm={4}><TextField label="ZIP Code" value={form.billingContact.postalCode} onChange={updateBilling('postalCode')} fullWidth /></Grid>
          </Grid>
          <Divider />
          <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Payment Method</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              A credit card on file is required before we launch your campaigns. This card will only be charged per the terms of your signed agreement.
            </Typography>
            {cardSaved ? (
              <Alert severity="success" variant="outlined" icon={<CheckCircleIcon />}>
                <strong>Card on file:</strong>{' '}
                {cardStatus?.card ? <>{cardStatus.card.brand?.toUpperCase()} ending in {cardStatus.card.last4} (exp {cardStatus.card.exp_month}/{cardStatus.card.exp_year})</> : 'Payment method saved'}
              </Alert>
            ) : clientSecret && stripePromise ? (
              <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
                <CardCollectionForm clientSecret={clientSecret} onSuccess={(card) => { setCardSaved(true); setCardStatus((prev) => ({ ...prev, has_card: true, card })); setClientSecret(null); handleSave(); }} />
              </Elements>
            ) : (
              <Button variant="outlined" onClick={handleSetupCard} disabled={!form.billingContact.name || !form.billingContact.email} sx={{ textTransform: 'none' }}>
                Add Credit Card
              </Button>
            )}
            {!cardSaved && !clientSecret && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {(!form.billingContact.name || !form.billingContact.email) ? 'Fill in the billing contact name and email above first.' : 'Click to securely add a card. No charges will be made now.'}
              </Typography>
            )}
          </Box>
        </Stack>
      );

      case 'assets': return (
        <Stack spacing={3}>
          <Typography variant="h6">Creative Assets</Typography>
          <Typography variant="body2" color="text.secondary">
            Upload logos, images, and brand materials for each brand. Each brand may have its own assets.
          </Typography>
          {children.map((child) => {
            const ba = form.brandAssets[child.slug] || {};
            return (
              <Accordion key={child.slug} defaultExpanded={children.length <= 3}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography fontWeight={600}>{child.name}</Typography>
                    {(ba.existingAssets?.length > 0 || ba.assets_folder_link || ba.use_stock_assets) && (
                      <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 18 }} />
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    {/* Existing assets */}
                    {ba.existingAssets?.length > 0 && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Uploaded files:</Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {ba.existingAssets.map((a) => (
                            <Chip key={a.id} label={a.name} size="small" variant="outlined" onDelete={() => {
                              // Mark for removal — will be handled on save
                              setForm((prev) => {
                                const updated = { ...prev.brandAssets };
                                updated[child.slug] = {
                                  ...updated[child.slug],
                                  existingAssets: (updated[child.slug]?.existingAssets || []).filter((x) => x.id !== a.id),
                                };
                                return { ...prev, brandAssets: updated };
                              });
                            }} />
                          ))}
                        </Stack>
                      </Box>
                    )}
                    {/* File upload */}
                    <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} sx={{ alignSelf: 'flex-start' }}>
                      Upload Files
                      <input type="file" hidden multiple onChange={(e) => {
                        const files = Array.from(e.target.files);
                        setForm((prev) => {
                          const updated = { ...prev.brandAssets };
                          updated[child.slug] = { ...updated[child.slug], files: [...(updated[child.slug]?.files || []), ...files] };
                          return { ...prev, brandAssets: updated };
                        });
                      }} />
                    </Button>
                    {ba.files?.length > 0 && (
                      <Typography variant="caption" color="text.secondary">{ba.files.length} file(s) ready to upload on save</Typography>
                    )}
                    {/* Folder link */}
                    <TextField
                      label="Google Drive / Dropbox Link"
                      value={ba.assets_folder_link || ''}
                      onChange={(e) => setForm((prev) => {
                        const updated = { ...prev.brandAssets };
                        updated[child.slug] = { ...updated[child.slug], assets_folder_link: e.target.value };
                        return { ...prev, brandAssets: updated };
                      })}
                      fullWidth size="small" placeholder="https://drive.google.com/..."
                    />
                    <FormControlLabel
                      control={<Checkbox checked={ba.use_stock_assets || false} onChange={(e) => setForm((prev) => {
                        const updated = { ...prev.brandAssets };
                        updated[child.slug] = { ...updated[child.slug], use_stock_assets: e.target.checked };
                        return { ...prev, brandAssets: updated };
                      })} />}
                      label="Use stock assets for this brand"
                    />
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      );

      case 'offers': return (
        <Stack spacing={3}>
          <Typography variant="h6">Offers & Messaging</Typography>
          <Typography variant="body2" color="text.secondary">
            What offers and messaging should we use across your brands?
          </Typography>
          {clientVerticals.length > 0 ? (
            clientVerticals.map((vert) => {
              const adKey = `${vert.id}_offer`;
              const finKey = `${vert.id}_financing`;
              const adEntry = form.offers[adKey] || {};
              const finEntry = form.offers[finKey] || {};
              const vertOptions = offerOptions.by_vertical?.[vert.id] || {};
              const adOptions = vertOptions.ad || offerOptions.fallback_ad || [];
              const finOptions = vertOptions.financing || offerOptions.fallback_financing || [];

              return (
                <Card key={vert.id} variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>{vert.name}</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select label="Ad Offer" fullWidth size="small"
                        value={adEntry.selected_label || adEntry.ad_offer_option_id || ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setForm((prev) => ({
                            ...prev,
                            offers: { ...prev.offers, [adKey]: { vertical_id: vert.id, offer_type: 'offer', selected_label: val, custom_label: '' } },
                          }));
                        }}
                      >
                        <MenuItem value="">Select an offer...</MenuItem>
                        {adOptions.map((o) => <MenuItem key={o.label} value={o.label}>{o.label}</MenuItem>)}
                        <MenuItem value="__custom">Custom offer...</MenuItem>
                      </TextField>
                      {(adEntry.selected_label === '__custom' || adEntry.custom_label) && (
                        <TextField
                          label="Custom Offer" fullWidth size="small" sx={{ mt: 1 }}
                          value={adEntry.custom_label || ''}
                          onChange={(e) => setForm((prev) => ({
                            ...prev,
                            offers: { ...prev.offers, [adKey]: { ...prev.offers[adKey], custom_label: e.target.value, selected_label: '' } },
                          }))}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select label="Financing Offer" fullWidth size="small"
                        value={finEntry.selected_label || finEntry.financing_offer_option_id || ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setForm((prev) => ({
                            ...prev,
                            offers: { ...prev.offers, [finKey]: { vertical_id: vert.id, offer_type: 'financing', selected_label: val, custom_label: '' } },
                          }));
                        }}
                      >
                        <MenuItem value="">Select financing...</MenuItem>
                        {finOptions.map((o) => <MenuItem key={o.label} value={o.label}>{o.label}</MenuItem>)}
                        <MenuItem value="__custom">Custom financing...</MenuItem>
                      </TextField>
                      {(finEntry.selected_label === '__custom' || finEntry.custom_label) && (
                        <TextField
                          label="Custom Financing" fullWidth size="small" sx={{ mt: 1 }}
                          value={finEntry.custom_label || ''}
                          onChange={(e) => setForm((prev) => ({
                            ...prev,
                            offers: { ...prev.offers, [finKey]: { ...prev.offers[finKey], custom_label: e.target.value, selected_label: '' } },
                          }))}
                        />
                      )}
                    </Grid>
                  </Grid>
                </Card>
              );
            })
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}><TextField label="Evergreen Offer" value={form.evergreenOffer} onChange={(e) => setForm((prev) => ({ ...prev, evergreenOffer: e.target.value }))} fullWidth placeholder='e.g. "50% off install"' /></Grid>
              <Grid item xs={12}><TextField label="Financing Offer" value={form.financingOffer} onChange={(e) => setForm((prev) => ({ ...prev, financingOffer: e.target.value }))} fullWidth placeholder='e.g. "12 months no interest, $0 down"' /></Grid>
            </Grid>
          )}
        </Stack>
      );

      case 'websites': return (
        <Stack spacing={3}>
          <Typography variant="h6">Brand Websites</Typography>
          <Typography variant="body2" color="text.secondary">
            Enter the website URL for each brand. This is used to generate tailored self-book confirmation pages.
          </Typography>
          {children.map((child) => {
            const ba = form.brandAssets[child.slug] || {};
            return (
              <Stack key={child.slug} direction="row" spacing={2} alignItems="center">
                <Typography variant="subtitle2" fontWeight={600} sx={{ minWidth: 140 }}>{child.name}</Typography>
                <TextField
                  value={ba.websiteUrl || ''}
                  onChange={(e) => setForm((prev) => {
                    const updated = { ...prev.brandAssets };
                    updated[child.slug] = { ...updated[child.slug], websiteUrl: e.target.value };
                    return { ...prev, brandAssets: updated };
                  })}
                  fullWidth size="small"
                  placeholder="https://www.yourbrand.com"
                />
              </Stack>
            );
          })}
        </Stack>
      );

      case 'schedule': return (
        <Stack spacing={3}>
          <Typography variant="h6">Sales Schedule</Typography>
          <Typography variant="body2" color="text.secondary">
            Set the availability for each brand. This is used to create self-book calendars for your leads.
          </Typography>

          {children.length > 1 && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">Same schedule for all brands?</Typography>
                <Button size="small" variant="outlined" onClick={() => {
                  const first = form.brandAssets[children[0]?.slug];
                  if (!first?.salesSchedule) { toast.error('Fill in the first brand\'s schedule first'); return; }
                  setForm((prev) => {
                    const updated = { ...prev.brandAssets };
                    children.forEach((c) => {
                      updated[c.slug] = { ...updated[c.slug], salesSchedule: first.salesSchedule };
                    });
                    return { ...prev, brandAssets: updated };
                  });
                  toast.success('Schedule applied to all brands');
                }}>
                  Apply First Schedule to All
                </Button>
              </Stack>
            </Alert>
          )}

          {children.map((child) => {
            const ba = form.brandAssets[child.slug] || {};
            return (
              <Accordion key={child.slug} defaultExpanded={children.length <= 3}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography fontWeight={600}>{child.name}</Typography>
                    {ba.salesSchedule && <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 18 }} />}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <SalesScheduleGrid
                    value={ba.salesSchedule || ''}
                    onChange={(val) => setForm((prev) => {
                      const updated = { ...prev.brandAssets };
                      updated[child.slug] = { ...updated[child.slug], salesSchedule: val };
                      return { ...prev, brandAssets: updated };
                    })}
                  />
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      );

      case 'review': return (
        <Stack spacing={3}>
          <Typography variant="h6">Review & Submit</Typography>
          <Typography variant="body2" color="text.secondary">
            Review your onboarding information for {parentName} ({brandNames}). You can come back and edit at any time.
          </Typography>

          <Card variant="outlined"><CardContent>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Contact Info</Typography>
            <Typography variant="body2">{form.basics.firstName} {form.basics.lastName} — {form.basics.email}</Typography>
          </CardContent></Card>

          <Card variant="outlined"><CardContent>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Billing</Typography>
            <Typography variant="body2">{form.billingContact.name} — {form.billingContact.email}</Typography>
            <Typography variant="body2">{cardSaved ? 'Card on file' : 'No card on file'}</Typography>
          </CardContent></Card>

          <Card variant="outlined"><CardContent>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Assets</Typography>
            {children.map((c) => {
              const ba = form.brandAssets[c.slug] || {};
              const hasAssets = ba.existingAssets?.length > 0 || ba.assets_folder_link || ba.use_stock_assets;
              return (
                <Typography key={c.slug} variant="body2">
                  {c.name}: {hasAssets ? <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 16, verticalAlign: 'middle' }} /> : 'None yet'}
                </Typography>
              );
            })}
          </CardContent></Card>

          <Divider />

          <DatePicker
            label="Target Launch Date"
            value={form.launchDate}
            onChange={(v) => setForm((prev) => ({ ...prev, launchDate: v }))}
            slotProps={{ textField: { fullWidth: true, helperText: "When would you like your campaigns to go live?" } }}
            minDate={dayjs()}
          />

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Button
              onClick={async () => { await handleSave(true); setSubmitted(true); }}
              variant="contained" size="large" disabled={saving}
              sx={{ bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' }, px: 6 }}
            >
              {saving ? 'Submitting...' : 'Submit Onboarding'}
            </Button>
          </Box>
        </Stack>
      );

      default: return null;
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      <Helmet><title>Onboarding — {parentName} | {config.SITE.title}</title></Helmet>

      {/* Header */}
      <Box sx={{ bgcolor: '#111827', py: 2, px: 3 }}>
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <img src={leadGurusLogo} alt="Lead Gurus" style={{ height: 28 }} />
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" sx={{ color: '#9ca3af' }}>{parentName}</Typography>
              <Button size="small" startIcon={<LogoutIcon />} onClick={logout} sx={{ color: '#9ca3af', textTransform: 'none', fontSize: 12 }}>Logout</Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={3}>
          {/* Sidebar */}
          <Grid item xs={12} md={3}>
            <Card sx={{ position: 'sticky', top: 16 }}>
              <CardContent>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  {parentName}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  {brandNames}
                </Typography>
                <Stepper activeStep={activeStep} orientation="vertical" nonLinear
                  sx={{ '& .MuiStepLabel-label': { fontSize: 13 } }}>
                  {STEPS.map((step, i) => {
                    const done = completion[step.key];
                    return (
                      <Step key={step.key} completed={done}>
                        <StepButton onClick={() => { handleSave(); setActiveStep(i); }}
                          icon={done ? <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 22 }} /> : undefined}>
                          {step.label}
                        </StepButton>
                      </Step>
                    );
                  })}
                </Stepper>
              </CardContent>
            </Card>
          </Grid>

          {/* Main content */}
          <Grid item xs={12} md={9}>
            <Card>
              <CardContent sx={{ p: { xs: 2, md: 4 } }}>
                {stepContent()}
              </CardContent>
            </Card>

            {/* Navigation */}
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
              <Button onClick={handleBack} disabled={activeStep === 0} startIcon={<NavigateBeforeIcon />}>Back</Button>
              <Stack direction="row" spacing={1}>
                <Button onClick={() => handleSave()} startIcon={<SaveIcon />} disabled={saving} variant="outlined">
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                {activeStep < STEPS.length - 1 && (
                  <Button onClick={handleNext} endIcon={<NavigateNextIcon />} variant="contained" disabled={saving}
                    sx={{ bgcolor: '#ff680d', '&:hover': { bgcolor: '#ea580c' } }}>
                    Next
                  </Button>
                )}
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
